import { Controller } from "@hotwired/stimulus"
import { areaStore } from '../../stores/locales/area_store'
import { deviceTypeStore } from '../../stores/device_types/device_type_store'
import { useStore } from 'stimulus-store';
import { isEmpty } from "lodash";
import { paperTypeStore } from "../../stores/print_mapping/paper_type_store";
import { getPaperTypes } from "../../packs/print_mapping/paper_types";
import { getPrintClasses } from "../../packs/print_mapping/print_classes";
import { printClassStore } from "../../stores/print_mapping/print_class_store";
import { printerStore } from "../../stores/devices/printer_store";
import { getPrinters } from "shared/selects/devices";
import { sendFailureNotification } from "shared/toast_notifications";

// Connects to data-controller="forms--print-mapping-widget"
//
// TURBO DRIVE NOTE: The stores used here (deviceTypeStore, areaStore,
// paperTypeStore, printerStore, printClassStore) are module-level singletons
// that persist across Turbo navigations. areaStore is cleared by
// area_select_controller's own disconnect() when leaving the previous page
// (see region_select_controller.js), so onAreaStoreUpdate shouldn't fire with
// stale data here; deviceTypeStore, printerStore, printClassStore, and
// paperTypeStore may still need explicit clearing if stale state causes issues.
export default class extends Controller {

    static stores = [
        deviceTypeStore,
        areaStore,
        paperTypeStore,
        printerStore,
        printClassStore
    ];

    // These all handle update page initialization
    static values = {
        initialDeviceTypeId: String,
        initialRegionId: String,
        initialBuildingId: String,
        initialAreaId: String
    };

    connect() {
        // Subscribe this controller to the stores in the stores array.
        useStore(this);

        // Grab the paper types and print classes.
        // This is safe since these can't change on pages that use this widget.
        this._getPaperTypes();
        this._getPrintClasses();

        if (this.hasContainerTarget) {
            this.containerTarget.classList.add('d-none');
        }
    }

    /**
     * Store Integration: Device type store callback
     *
     * Called automatically by stimulus-store when deviceTypeStore updates.
     * Updates widget visibility based on whether device type is a workstation.
     *
     * Requirements: 2.2, 2.3, 2.4, 2.5
     *
     * @param {Object} event - Device type store state
     */
    onDeviceTypeStoreUpdate(event) {
        // Update visibility when device type changes
        this.handleVisibilityChange();
    }

    /**
     * Store Integration: Area store callback
     *
     * Called automatically by stimulus-store when areaStore updates.
     * Validates area data structure and fetches printers for the selected area.
     * Clears printer store when area is empty or invalid.
     *
     * Requirements: 1.2, 1.3, 4.1, 4.2, 4.3
     *
     * @param {Object} area - Area store state
     */
    onAreaStoreUpdate(area) {
        // Guard: Ensure store setter is initialized before proceeding
        // This can be called during useStore initialization before setters are ready
        if (!this.setPrinterStoreValue) {
            return;
        }

        // Validate area data structure (Requirements 1.3, 4.3)
        // Handle empty or cleared area store - clear printer store
        if (!area || isEmpty(area)) {
            // Clear printer store when area is empty (printerStore uses object format)
            this.setPrinterStoreValue({ printers: [] });
            return;
        }

        // Extract region, building, and area IDs safely
        const region = area.region;
        const building = area.building;
        const areaId = area.id;

        // Only fetch printers when all required IDs are present (Requirements 1.2, 4.1, 4.2)
        if (!region || !building || !areaId) {
            // Clear printer store when required IDs are missing (printerStore uses object format)
            this.setPrinterStoreValue({ printers: [] });
            return;
        }

        // Fetch printers for the valid area
        this._getPrinters(region, building, areaId);
    }

    /**
     * Data Fetching: Fetch paper types with error handling
     *
     * Fetches paper types from the API and updates the paper type store.
     * Handles errors gracefully with toast notifications.
     * Updates store with a promise that resolves to the paper types array.
     *
     * Requirements: 1.5, 6.3
     */
    async _getPaperTypes() {
        try {
            // Await the promise to catch any errors
            const paperTypes = await getPaperTypes();
            // Update store with resolved promise containing the data
            this.setPaperTypeStoreValue(paperTypes);
        } catch (error) {
            this.handleFetchError(error, 'paper types');
            // Set empty array to allow user to continue without paper types
            this.setPaperTypeStoreValue([]);
        }
    }

    /**
     * Data Fetching: Fetch print classes with error handling
     *
     * Fetches print classes from the API and updates the print class store.
     * Handles errors gracefully with toast notifications.
     * Updates store with a promise that resolves to the print classes array.
     *
     * Requirements: 1.6, 6.2
     */
    async _getPrintClasses() {
        try {
            // Await the promise to catch any errors
            const printClasses = await getPrintClasses();
            // Update store with resolved promise containing the data
            this.setPrintClassStoreValue(printClasses);
        } catch (error) {
            this.handleFetchError(error, 'print classes');
            // Set empty array to allow user to continue without print classes
            this.setPrintClassStoreValue([]);
        }
    }

    /**
     * Data Fetching: Fetch printers with error handling
     *
     * Fetches printers for the specified location from the API and updates the printer store.
     * Handles errors gracefully with toast notifications.
     * getPrinters already returns object format { printers: [...] } as expected by printerStore.
     *
     * Requirements: 1.7, 6.1
     *
     * @param {String} region - Region ID
     * @param {String} building - Building ID
     * @param {String} area - Area ID
     */
    async _getPrinters(region, building, area) {
        try {
            // Await the promise to catch any errors
            // getPrinters already returns { printers: [...] } format
            const printersResult = await getPrinters(region, building, area);
            // Update store with the result (already in correct format)
            this.setPrinterStoreValue(printersResult);
        } catch (error) {
            this.handleFetchError(error, 'printers');
            // Set empty array in object format to prevent printer selection
            this.setPrinterStoreValue({ printers: [] });
        }
    }

    /**
     * Workstation Detection: Check if device type is a workstation
     *
     * Determines if the current device type is a workstation by checking
     * the workstation property in the device type.
     *
     * Requirements: 2.2, 2.3, 2.6, 2.7
     *
     * @returns {Boolean} True if device type is a workstation, false otherwise
     */
    isWorkstation() {
        return this.deviceTypeStoreValue?.workstation === true;
    }

    /**
     * Visibility Management: Update widget visibility
     *
     * Shows or hides the widget based on whether the device type is a workstation.
     * Uses Bootstrap d-none class for consistent styling.
     * Preserves form data when hiding (no DOM removal).
     *
     * Requirements: 2.2, 2.3, 2.4, 2.5, 2.6, 2.7
     */
    updateVisibility() {
        const container = this.element;

        if (!container) {
            return;
        }

        const isWorkstation = this.isWorkstation();

        if (isWorkstation) {
            // Show widget (Requirements 2.2, 2.5, 2.6)
            container.classList.remove('d-none');
        } else {
            // Hide widget (Requirements 2.3, 2.4, 2.7)
            container.classList.add('d-none');
        }
    }

    /**
     * Visibility Management: Coordinate visibility updates
     *
     * Handles visibility changes when device type changes.
     * Coordinates the visibility update process.
     *
     * Requirements: 2.2, 2.3, 2.4, 2.5, 2.6, 2.7
     */
    handleVisibilityChange() {
        this.updateVisibility();
    }

    /**
     * Error Handling: Handle fetch errors with toast notifications
     *
     * Centralized error handling for all fetch operations.
     * Logs errors to console with context and displays user-friendly
     * toast notifications using sendFailureNotification.
     *
     * Requirements: 6.1, 6.2, 6.3
     *
     * @param {Error} error - The error object
     * @param {String} context - Context string ('paper types', 'print classes', or 'printers')
     */
    handleFetchError(error, context) {
        // Log error to console with context for debugging
        console.error(`Error fetching ${ context }:`, error);

        // Context-specific error messages
        const messages = {
            'paper types': 'Unable to load paper types. Please refresh the page or contact support.',
            'print classes': 'Unable to load print classes. Please refresh the page or contact support.',
            'printers': 'Unable to load printers for the selected area. Please try selecting a different area or refresh the page.'
        };

        // Display user-friendly toast notification
        const message = messages[context] || 'An error occurred. Please try again.';
        sendFailureNotification(message);
    }

    updatePrintMappingHeaderVisibility(event) {
        if (this.hasPrintMappingHeaderTarget) {
            // Get the "Print mapping" header
            const printMappingHeader = this.printMappingHeaderTarget;

            // If the selected device type is a workstation or a printer, this header
            // should be shown. If it's not, then it shouldn't be
            if (event.detail === undefined) {
                // This happens if the event was issued without a payload. This is typical of an
                // deviceTypeSelectionCleared event. In this case, hide the header
                printMappingHeader.style.display = 'none';
            } else if (event.detail.printer || event.detail.workstation) {
                // This style shows this element and it's children
                printMappingHeader.style.display = 'block';
            } else {
                // This style hides this element and its children
                printMappingHeader.style.display = 'none';
            }
        }
    }

    updateTrayMappingWidgetVisibility(event) {
        // We can only modify the visibility of this widget if it's present
        if (this.hasTrayMappingTarget) {
            // Get the tray mapping widget
            const trayMappingWidget = this.trayMappingTarget;

            // Determine if the newly selected device type is a printer. If it is,
            // show this widget. If it's not, hide it.
            if (event.detail === undefined) {
                // This happens if the event was issued without a payload. This is typical of an
                // deviceTypeSelectionCleared event. In this case, hide the header
                trayMappingWidget.style.display = 'none';
            } else if (event.detail.printer) {
                // This style shows this element and it's children
                trayMappingWidget.style.display = 'block';
            } else {
                // This style hides this element and its children
                trayMappingWidget.style.display = 'none';
            }
        }
    }

    updatePrintMappingWidgetVisibility() {
        // We can only modify the visibility of this widget if it's present
        if (this.hasPrintMappingTarget) {
            // Get the print mapping widget
            const printMappingWidget = this.printMappingTarget;

            // Determine if the newly selected device type is a workstation. If it is,
            // show this widget. If it's not, hide it.
            // Determine if the newly selected device type is a printer. If it is,
            // show this widget. If it's not, hide it.
            if (event.detail === undefined) {
                // This happens if the event was issued without a payload. This is typical of an
                // deviceTypeSelectionCleared event. In this case, hide the header
                printMappingWidget.style.display = 'none';
            } else if (event.detail.workstation) {
                printMappingWidget.style.display = 'block';
            } else {
                printMappingWidget.style.display = 'none';
            }
        }
    }
}